import type { TFunction } from 'i18next';
import { FileText, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@shared/components/ui/cn';

export function formatFileSize(bytes: number, t: TFunction<'ai-cio'>): string {
  if (bytes < 1024) return t('attachment.sizeBytes', '{{value}} B', { value: bytes.toString() });
  if (bytes < 1024 * 1024)
    return t('attachment.sizeKilobytes', '{{value}} KB', { value: (bytes / 1024).toFixed(1) });
  return t('attachment.sizeMegabytes', '{{value}} MB', {
    value: (bytes / (1024 * 1024)).toFixed(1),
  });
}

export function fileExtension(name: string, t: TFunction<'ai-cio'>): string {
  const ext = name.split('.').pop();
  return ext && ext !== name ? ext.toUpperCase() : t('attachment.typeFallback', 'FILE');
}

interface AttachmentChipProps {
  name: string;
  size: number;
  onRemove?: () => void;
  removeLabel?: string;
  className?: string;
}

export function AttachmentChip({
  name,
  size,
  onRemove,
  removeLabel,
  className,
}: AttachmentChipProps) {
  const { t } = useTranslation('ai-cio');
  return (
    <div
      className={cn(
        'flex w-56 max-w-full items-center gap-2.5 rounded-lg border bg-background px-2.5 py-2',
        className,
      )}
    >
      <div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
        <FileText className="size-4" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-xs font-medium text-foreground" title={name}>
          {name}
        </p>
        <p className="mt-0.5 text-[10px] text-muted-foreground">
          {fileExtension(name, t)} · {formatFileSize(size, t)}
        </p>
      </div>
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={removeLabel ?? t('attachment.remove', 'Remove')}
          title={removeLabel ?? t('attachment.remove', 'Remove')}
          className="flex size-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-destructive"
        >
          <X className="size-3.5" />
        </button>
      )}
    </div>
  );
}
